import { UnreachableCaseError } from 'ts-essentials';
import { geoValuesEqual, RGB_MAX_VALUE } from '@trimble/utilities';
import {
  ArrowMarkup,
  CloudMarkup,
  LineMarkup,
  Markup,
  RedlinesMarkup,
  TextMarkup,
} from './markup';

function markupColorsEqual(
  a: Markup['color'] | undefined,
  b: Markup['color'] | undefined
): boolean {
  if (a === undefined && b === undefined) {
    return true;
  }
  if (a === undefined || b === undefined) {
    return false;
  }
  return (
    a.r === b.r &&
    a.g === b.g &&
    a.b === b.b &&
    (a.a ?? RGB_MAX_VALUE) === (b.a ?? RGB_MAX_VALUE)
  );
}

function positionsEqual(
  x1: number,
  y1: number,
  z1: number,
  x2: number,
  y2: number,
  z2: number
): boolean {
  return (
    geoValuesEqual(x1, x2) && geoValuesEqual(y1, y2) && geoValuesEqual(z1, z2)
  );
}

const CLOUD_NORMAL_EPSILON = 1e-10;

function normalizeCloudNormal(
  planeA: number,
  planeB: number,
  planeC: number
): readonly [number, number, number] {
  const len = Math.hypot(planeA, planeB, planeC);
  if (len > CLOUD_NORMAL_EPSILON) {
    return [planeA / len, planeB / len, planeC / len];
  }
  return [0, 0, 1];
}

function cloudNormalsEqual(a: CloudMarkup, b: CloudMarkup): boolean {
  const [ax, ay, az] = normalizeCloudNormal(a.planeA, a.planeB, a.planeC);
  const [bx, by, bz] = normalizeCloudNormal(b.planeA, b.planeB, b.planeC);
  return (
    geoValuesEqual(ax, bx) && geoValuesEqual(ay, by) && geoValuesEqual(az, bz)
  );
}

/**
 * Returns true when viewer-derived canonical geometry differs from the stored item.
 * Used to skip cross-pane resync for seed/replication noise (NUMARK-899).
 *
 * Compares only fields populated by {@link convertViewerMarkupToMarkup}. Text
 * width/screenSpaceDistance and cloud planeD are omitted; cloud normals are
 * compared as unit directions because the converter normalizes them.
 */
export function hasMarkupGeometryChanged(
  existing: Markup,
  updated: Markup
): boolean {
  if (existing.type !== updated.type) {
    return true;
  }

  if (!markupColorsEqual(existing.color, updated.color)) {
    return true;
  }

  switch (existing.type) {
    case 'cloud': {
      const next = updated as CloudMarkup;
      return (
        !positionsEqual(
          existing.positionX,
          existing.positionY,
          existing.positionZ,
          next.positionX,
          next.positionY,
          next.positionZ
        ) ||
        !cloudNormalsEqual(existing, next) ||
        !geoValuesEqual(existing.width, next.width) ||
        !geoValuesEqual(existing.height, next.height)
      );
    }
    case 'line':
    case 'arrow': {
      const next = updated as LineMarkup | ArrowMarkup;
      return (
        !positionsEqual(
          existing.positionX,
          existing.positionY,
          existing.positionZ,
          next.positionX,
          next.positionY,
          next.positionZ
        ) ||
        !positionsEqual(
          existing.position2X,
          existing.position2Y,
          existing.position2Z,
          next.position2X,
          next.position2Y,
          next.position2Z
        )
      );
    }
    case 'text': {
      const next = updated as TextMarkup;
      return (
        !positionsEqual(
          existing.positionX,
          existing.positionY,
          existing.positionZ,
          next.positionX,
          next.positionY,
          next.positionZ
        ) ||
        !positionsEqual(
          existing.position2X,
          existing.position2Y,
          existing.position2Z,
          next.position2X,
          next.position2Y,
          next.position2Z
        ) ||
        existing.text !== next.text
      );
    }
    case 'redlines': {
      const next = updated as RedlinesMarkup;
      if (existing.lines.length !== next.lines.length) {
        return true;
      }
      return existing.lines.some((line, index) => {
        // Lengths match above; index is always in bounds for both arrays.
        const other = next.lines[index]!;
        return (
          !positionsEqual(
            line.positionX,
            line.positionY,
            line.positionZ,
            other.positionX,
            other.positionY,
            other.positionZ
          ) ||
          !positionsEqual(
            line.position2X,
            line.position2Y,
            line.position2Z,
            other.position2X,
            other.position2Y,
            other.position2Z
          )
        );
      });
    }
    default:
      throw new UnreachableCaseError(existing);
  }
}
